import { useCallback, useEffect, useRef, useState } from 'react';

import styles from '../PurchaseModal.module.scss';

const GENERIC_MODAL_PADDING = parseInt(styles.genericModalPadding);
const PADDING_BOTTOM_WHEN_KEYBOARD_OPEN = parseInt(styles.paddingBottomWhenKeyboardOpen);
const BREAKPOINT_MOBILE = parseInt(styles.breakpointMobile);

const setVisibleViewportHeight = (height) => {
	document.documentElement.style.setProperty('--viewport-visible-height', `${height}px`);
};

const observeHeightChange = [styles.vehicleName, styles.dotList];

const VisualViewportKeyboardObserver = ({ children }) => {
	const [state, setState] = useState({
		height: 0,
		isKeyboardOpen: false,
		isLandscape: false,
		isMobile: false,
		isTouchDevice: false,
		isVisualViewport: false,
		ready: false,
		width: 0,
	});

	// we use ref here to do not perform unnecessary rerenders
	// it's essentially the instance variable usage of refs
	// this comment also covers usages below
	// ref: https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
	const modalContentsHeight = useRef({
		footerContainer: 0,
		infoContainer: 0,
		total: 0,
	});

	const initialDimensions = useRef({
		height: 0,
		width: 0,
	});

	const elements = useRef([]);

	// we need to wait for dialog content to be mounted to DOM in order to run calculations
	const onModalMount = useCallback(() => {
		if (window.visualViewport) {
			const visualViewportData = window.visualViewport;
			const { height, width } = visualViewportData;

			initialDimensions.current = { height, width };

			setState((prevState) => ({
				...prevState,
				height,
				isLandscape: window.matchMedia('(orientation: landscape)').matches,
				isTouchDevice: window.matchMedia('(pointer: coarse)').matches,
				isVisualViewport: true,
				ready: true,
				width,
			}));
		}
	}, []);

	useEffect(() => {
		const getModalElements = () => {
			const containerClass = `.${styles.vehiclePurchaseModal} .${styles.dialogWrapper} > div`;
			const infoContainer = document.querySelector(`${containerClass} .${styles.infoContainer}`);
			const footerContainer = document.querySelector(`${containerClass} .${styles.footer}`);

			if (infoContainer && footerContainer) {
				const { height: infoContainerHeight } = infoContainer.getBoundingClientRect();
				const { height: footerContainerHeight } = footerContainer.getBoundingClientRect();

				const infoContainerAbsoluteHeight = infoContainerHeight + GENERIC_MODAL_PADDING;
				const footerContainerAbsoluteHeight = footerContainerHeight + GENERIC_MODAL_PADDING;
				const total = infoContainerAbsoluteHeight + footerContainerAbsoluteHeight;

				// store modal elements sizes to be able to use them later
				modalContentsHeight.current = {
					footerContainer: footerContainerAbsoluteHeight,
					infoContainer: infoContainerAbsoluteHeight,
					total,
				};

				elements.current = []; // Array.push + react fast refresh = duplications, so need to make sure array is clear

				// get elements info and perform calculations on saved data later
				[...infoContainer.children].forEach((el) => {
					const { height, top } = el.getBoundingClientRect();

					elements.current.push({
						el,
						height,
						observeHeight: observeHeightChange.some((c) => el.classList.contains(c)),
						top,
					});
				});

				// calculate absolute height of elements
				elements.current.forEach((el, i) => {
					const nextEl = elements.current[i + 1];

					if (nextEl) {
						const margin = nextEl.top - el.top - el.height;

						elements.current[i] = { ...el, margin };
					}
				});
			}
		};

		const visualViewportHandler = ({ srcElement: { height: viewportHeight, width: viewportWidth } }) => {
			setState((prevState) => {
				const isKeyboardOpen = initialDimensions.current.height > viewportHeight;
				const isLandscape = window.matchMedia('(orientation: landscape)').matches;
				const isMobile = viewportWidth <= BREAKPOINT_MOBILE;

				setVisibleViewportHeight(viewportHeight);

				const shouldUpdateElements =
					// adjust number of elements if modal has mobile styling and screen is smaller than content size
					(isMobile && viewportHeight <= modalContentsHeight.current.total) ||
					// adjust number of elements if modal has mobile styling and we closed keyboard
					(isMobile && prevState.isKeyboardOpen && !state.isKeyboardOpen);

				if (shouldUpdateElements) {
					// we need to start calculations from footer size and padding bottom value when keyboard is open.
					// vigilant eye can tell that we already added padding to footer, but in this calculation it is treated
					// as a top padding
					const elementSizeStartValue = modalContentsHeight.current.footerContainer + PADDING_BOTTOM_WHEN_KEYBOARD_OPEN;

					// it's the most expensive part of the keyboard handler logic.
					// there is probably a cheaper way to do it.
					// hopefully I haven't seen any performance issues with it
					elements.current.reduceRight((elementSize, element, i) => {
						const { el, height, observeHeight } = element;
						let actualHeight = height;

						if (observeHeight) {
							if (!el.getAttribute('data-mw-hidden')) {
								const { height: elHeight } = el.getBoundingClientRect();
								const heightDifference = elHeight - height;

								actualHeight = elHeight;
								modalContentsHeight.current.total += heightDifference;

								elements.current[i] = { ...element, height: actualHeight };
							}
						}

						const { margin = GENERIC_MODAL_PADDING } = elements.current[i - 1] || {};
						const nextElementsSize = elementSize + actualHeight + margin;
						const isVisible = nextElementsSize <= viewportHeight;

						el[`${isVisible ? 'remove' : 'set'}Attribute`]('data-mw-hidden', 'true');

						return nextElementsSize;
					}, elementSizeStartValue);
				}

				if (!isLandscape) {
					setTimeout(() => {
						window.scrollTo(0, 0);
						setTimeout(() => window.scrollTo(0, 0), 1000); // delay to make sure browser will scroll to the top
					});
				}

				return {
					...prevState,
					height: viewportHeight,
					isKeyboardOpen,
					isLandscape,
					isMobile,
					width: viewportWidth,
				};
			});
		};

		if (state.isTouchDevice && state.ready) {
			getModalElements();
			visualViewportHandler({ srcElement: state });

			window.visualViewport?.addEventListener('resize', visualViewportHandler);
		}

		return () => {
			window.visualViewport?.removeEventListener('resize', visualViewportHandler);
		};
	}, [state.ready]); // eslint-disable-line react-hooks/exhaustive-deps

	return children({ ...state, onModalMount });
};

export default VisualViewportKeyboardObserver;
